/**
 * DataBest — EndAssignmentPopover
 *
 * Popover for ending an active assignment with an optional backdated
 * effective_to. Warns the supervisor if there are pending backlog items
 * inside the window (they will be counted as coverage_gap in the caregiver's
 * performance report).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, StopCircle } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import DatePickerUS from "@/components/DatePickerUS";
import { assignmentsApi } from "../api/assignmentsApi";
import { todayISO } from "@/lib/dates";

const EndAssignmentPopover = ({ assignment, onEnded }) => {
    const [open, setOpen] = useState(false);
    const [endDate, setEndDate] = useState(todayISO());
    const [saving, setSaving] = useState(false);
    const [pendingCount, setPendingCount] = useState(null);
    const [pendingLoading, setPendingLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEndDate(todayISO());
        setPendingLoading(true);
        assignmentsApi
            .pendingBacklogCount(assignment.id)
            .then((r) => setPendingCount(r?.pending_count ?? 0))
            .catch(() => setPendingCount(null))
            .finally(() => setPendingLoading(false));
    }, [open, assignment.id]);

    const submit = async (chosenDate) => {
        setSaving(true);
        try {
            await assignmentsApi.end(assignment.id, chosenDate);
            toast.success("Assignment ended");
            setOpen(false);
            onEnded?.();
        } catch (err) {
            toast.error(err?.message || "Could not end assignment");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`assignments-end-btn-${assignment.id}`}
                    className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-800 hover:bg-amber-100"
                    title="End this assignment"
                >
                    <StopCircle className="h-3 w-3" />
                    End
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-80 p-0"
                align="end"
                data-testid={`assignments-end-popover-${assignment.id}`}
            >
                <div className="border-b border-slate-100 px-3 py-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        End assignment
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-slate-800">
                        {assignment.caregiver_name}
                        <span className="mx-1 text-slate-400">→</span>
                        {assignment.individual_name}
                    </p>
                </div>

                <div className="space-y-2.5 px-3 py-3">
                    {pendingLoading ? (
                        <p className="text-[11px] text-slate-400">
                            Checking pending backlog…
                        </p>
                    ) : pendingCount !== null && pendingCount > 0 ? (
                        <div
                            className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] font-semibold text-amber-900"
                            data-testid={`assignments-end-backlog-warning-${assignment.id}`}
                        >
                            <div className="mb-0.5 inline-flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                {pendingCount} pending backlog item
                                {pendingCount === 1 ? "" : "s"}
                            </div>
                            <p className="font-normal">
                                These will be counted as coverage gap in the
                                caregiver&apos;s performance report.
                            </p>
                        </div>
                    ) : null}

                    <div>
                        <label className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            End date
                        </label>
                        <DatePickerUS
                            value={endDate}
                            onChange={(iso) => iso && setEndDate(iso)}
                            min={assignment.effective_from}
                            max={todayISO()}
                            testId={`assignments-end-date-${assignment.id}`}
                            className="h-9 w-full"
                        />
                        <p className="mt-1 text-[10px] text-slate-400">
                            Backdating is allowed as long as it&apos;s not
                            before the start of the assignment or in the
                            future.
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 bg-slate-50/60 px-3 py-2">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        disabled={saving}
                        className="rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-60"
                        data-testid={`assignments-end-cancel-${assignment.id}`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => submit(todayISO())}
                        disabled={saving}
                        data-testid={`assignments-end-today-${assignment.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                    >
                        End today
                    </button>
                    <button
                        type="button"
                        onClick={() => submit(endDate)}
                        disabled={saving || !endDate}
                        data-testid={`assignments-end-confirm-${assignment.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-amber-700 disabled:opacity-60"
                    >
                        {saving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : null}
                        End on date
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default EndAssignmentPopover;
